import { Avatar, Button, Label, Radio } from 'flowbite-react';
import React, { useEffect } from 'react';
import { BulkActionByRecruiterInput } from 'types';
import { bulkAction } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { get } from 'lodash';
import RecruiterCandidateSendAssignment from 'src/components/recruiterPositions/RecruiterCandidateSendAssignment';
import RecruiterCandidateSendMessage from 'src/components/recruiterPositions/RecruiterCandidateSendMessage';
import RecruiterCandidateMoveToNextRound from 'src/components/recruiterPositions/RecruiterCandidateMoveToNextRound';
import RecruiterCandidateRejectCandidate from 'src/components/recruiterPositions/RecruiterCandidateRejectCandidate';
import RecruiterCandidateUndoAutoReject from 'src/components/recruiterPositions/RecruiterCandidateUndoAutoReject';
import BriefDebriefModal from 'src/components/recruiterPositions/BriefDebriefModal';
import RecruiterShareCandidates from 'src/components/recruiterPositions/RecruiterShareCandidate';
import { CandidatesTabsEnum } from 'src/components/recruiterPositions/RecruiterCandidatesContainer2';
import { useGetRecommendationsForPositionQuery } from 'src/hooks/position.hooks';
import dayjs from 'dayjs';
import { stringAvatar } from 'shared-utils';
import UsersAvatarGroup from './UsersAvatarGroup';
import RecruiterCandidateSendOnlineTest from 'src/components/recruiterPositions/RecruiterCandidateSendOnlineTest';

type BulkActionType = BulkActionByRecruiterInput['action'];

type Props = {
	selectedTab: string;
	bulkSelectedCandidates: string[];
	onFinish: () => void;
	positionId?: string;
	defaultBulkAction?: string | null;
};

const NewRecruiterCandidatesBulkActionsOnConncetedUsers = ({
	selectedTab,
	positionId,
	bulkSelectedCandidates,
	onFinish,
	defaultBulkAction = null,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [selectedBulkAction, setSelectedBulkAction] = React.useState<BulkActionType | string | null>();
	const [localActionSelect, setLocalActionSelect] = React.useState<BulkActionType | string | null>();

	const { data: positionRecomendationData } = useGetRecommendationsForPositionQuery({
		positionId: positionId ?? '',
	});

	const positionHasExpired = dayjs().isAfter(positionRecomendationData?.position?.expiresAt);

	const bulkSelectedCandidatesData = [
		...(positionRecomendationData?.recommendedUsers || []),
		...(positionRecomendationData?.connectedUsers || []),
		...(positionRecomendationData?.interestedUsers || []),
		...(positionRecomendationData?.inPipeLineUsers || []),
	]?.filter(candidate => bulkSelectedCandidates?.includes(candidate?.user?._id?.toString()));

	const uniqueCandidates = Array.from(
		new Set(bulkSelectedCandidatesData.map(candidate => candidate.user._id.toString())),
	).map(id => bulkSelectedCandidatesData.find(candidate => candidate.user._id.toString() === id));

	useEffect(() => {
		setSelectedBulkAction(defaultBulkAction);
		setLocalActionSelect(defaultBulkAction);
	}, [bulkSelectedCandidates, defaultBulkAction]);

	const bulkActionFn = useMutation(['bulkAction'], bulkAction, {
		onSuccess: async (data: any) => {
			if (data.action === 'download-resumes') {
				const zip = new JSZip();
				for (let resume of data.resumes) {
					zip.file(resume.fileName, resume.content.data);
				}

				const zipBlob = await zip.generateAsync({ type: 'blob' });
				saveAs(zipBlob, 'resumes.zip');
			}

			queryClient.invalidateQueries(['getRecommendationsForPosition', positionId]);
			queryClient.refetchQueries(['getRecommendationsForPosition', positionId]);
			let toastMessage = 'Request Sent Successfully';

			if (selectedBulkAction === 'message') toastMessage = 'Message sent to Candidates';
			else if (selectedBulkAction === 'send-assignment') toastMessage = 'Assignment sent to Candidates';
			else if (selectedBulkAction === 'send-online-test') toastMessage = 'Online test sent to Candidates';
			else if (selectedBulkAction === 'move-to-next') toastMessage = 'Moved selected candidates to their next round';
			else if (selectedBulkAction === 'share-profile') toastMessage = 'Email sent successfully';
			else if (data?.action === 'download-resumes') toastMessage = 'Resumes downloaded successfully';
			else if (data?.message) toastMessage = data?.message;

			toast({
				title: toastMessage,
				type: 'success',
			});

			onFinish && onFinish();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			const match = errorMessage.match(/InvalidUserError: (.*)/);
			if (match) {
				toast({
					title: match[1],
					type: 'error',
				});
			} else {
				toast({
					title: errorMessage,
					type: 'error',
				});
			}
		},
	});

	const getDrawerContent = (value: string) => {
		switch (value) {
			case 'message': {
				return (
					<RecruiterCandidateSendMessage
						userName="Candidates"
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? ('message' as BulkActionType),
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
								message: data?.message,
							});
						}}
					/>
				);
			}
			case 'send-assignment': {
				return (
					<>
						<p className="text-xs italic mb-2 text-gray-500">
							Note: People who are already assigned an assignment, will not recive this assignment
						</p>
						<RecruiterCandidateSendAssignment
							positionId={positionId}
							isLoading={bulkActionFn.isLoading}
							onSubmit={data => {
								bulkActionFn.mutate({
									action: (selectedBulkAction as BulkActionType) ?? 'send-assignment',
									positionId: positionId ?? '',
									userIds: bulkSelectedCandidates,
									assignmentDeadline: data?.assignmentDeadline,
									assignmentDescription: data?.assignmentDescription,
									assignmentLink: data?.assignmentLink,
									assignmentFileLink: data?.assignmentFileLink,
									assignmentSubmissionType: data?.assignmentSubmissionType,
									assignmentLinkOptions: data?.assignmentLinkOptions,
									assignmentFileOptions: data?.assignmentFileOptions,
									assignmentSubmissionNotes: data?.assignmentSubmissionNotes,
								});
							}}
						/>
					</>
				);
			}
			case 'send-online-test': {
				return (
					<RecruiterCandidateSendOnlineTest
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'send-online-test',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
								onlineTestLink: data?.onlineTestLink,
								prepCoach: data?.prepCoach,
								remarks: data?.remarks,
							});
						}}
					/>
				);
			}

			case 'move-to-next': {
				return (
					<RecruiterCandidateMoveToNextRound
						userName="Candidates"
						showMessageInput={false}
						nextRoundLabel="Move to next round"
						positionId={positionId ?? ''}
						currentRound={0}
						totalRounds={0}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'move-to-next',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
							});
						}}
					/>
				);
			}
			case 'share-profile': {
				return (
					<RecruiterShareCandidates
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'share-profile',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
								emailIds: data?.emailIds,
								message: data?.message,
							});
						}}
					/>
				);
			}
			case 'bulk-reject': {
				return (
					<RecruiterCandidateRejectCandidate
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'bulk-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
								message: data?.remarks,
							});
						}}
					/>
				);
			}
			case 'bulk-undo-auto-reject': {
				return (
					<RecruiterCandidateUndoAutoReject
						userName="Candidates"
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'bulk-undo-auto-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
							});
						}}
					/>
				);
			}

			case 'download-resumes': {
				return (
					<RecruiterCandidateRejectCandidate
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'bulk-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
								message: data?.remarks,
							});
						}}
					/>
				);
			}

			case 'schedule-brief-debrief-call': {
				return (
					<BriefDebriefModal
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: (selectedBulkAction as BulkActionType) ?? 'schedule-brief-debrief-call',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates,
								message: data?.message,
								callType: data?.callType,
								meetingDateTime: data?.meetingDateTime,
								meetingLink: data?.meetingLink,
								duration: data?.duration,
								additionalEmails: data?.additionalEmails,
								roundNumber: data?.roundNumber,
								roundType: data?.roundType,
								prepCoach: data?.prepCoach,
								label: data?.label,
							});
						}}
					/>
				);
			}

			default:
				return (
					<form
						onSubmit={e => {
							e.preventDefault();
							if (localActionSelect === 'download-resumes') {
								bulkActionFn.mutate({
									action: 'download-resumes',
									userIds: bulkSelectedCandidates,
									positionId: positionId || '',
								});
							} else {
								setSelectedBulkAction(localActionSelect);
							}
						}}
					>
						<div className="mb-3 block">
							<Label htmlFor="action" className="font-[700] font-inter text-sm text-[#28293D]" value="Choose action" />
						</div>

						<fieldset
							className="grid grid-cols-2 max-w-md gap-4"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								if (target?.value) {
									setLocalActionSelect(target?.value as BulkActionByRecruiterInput['action']);
								}
							}}
						>
							{selectedTab === CandidatesTabsEnum.RECOMMENDATIONS &&
								!window?.location?.pathname.includes('pipeline') ? (
								<>
									<div className="flex items-center gap-2">
										<Radio
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'shareCandidates'}
											name="bulkAction"
											value={'share-profile'}
											checked={localActionSelect === 'share-profile'}
										/>
										<Label htmlFor={'shareCandidates'} className="text-sm font-[400] font-inter">
											Share Candidates
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											className="text-[#7777E7] focus:ring-[#7777E7] disabled:opacity-45 disabled:bg-[#d2d2d2]"
											id={'shortlistCandidates'}
											name="bulkAction"
											value={'shortlist-profile'}
											checked={localActionSelect === 'shortlist-profile'}
											disabled={positionHasExpired}
										/>
										<Label htmlFor={'shortlistCandidates'} className="text-sm font-[400] font-inter">
											Shortlist Candidates
										</Label>
									</div>
								</>
							) : (
								<>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'message'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'sendMessage'}
											name="bulkAction"
											value={'message'}
										/>
										<Label htmlFor={'sendMessage'} className="text-sm font-[400] font-inter">
											Send message
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'send-assignment'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'sendAssignment'}
											name="bulkAction"
											value={'send-assignment'}
										/>
										<Label htmlFor={'sendAssignment'} className="text-sm font-[400] font-inter">
											Send assignment
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'send-online-test'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'sendOnlineTest'}
											name="bulkAction"
											value={'send-online-test'}
										/>
										<Label htmlFor={'sendOnlineTest'} className="text-sm font-[400] font-inter">
											Send online test
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'share-profile'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'shareCandidates'}
											name="bulkAction"
											value={'share-profile'}
										/>
										<Label htmlFor={'shareCandidates'} className="text-sm font-[400] font-inter">
											Share Candidates
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'move-to-next'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'moveTo'}
											name="bulkAction"
											value={'move-to-next'}
										/>
										<Label htmlFor={'moveTo'} className="text-sm font-[400] font-inter">
											Move to
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'bulk-reject'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'bulkReject'}
											name="bulkAction"
											value={'bulk-reject'}
										/>
										<Label htmlFor={'bulkReject'} className="text-sm font-[400] font-inter">
											Bulk Reject
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'bulk-undo-auto-reject'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'bulkUndoAutoReject'}
											name="bulkAction"
											value={'bulk-undo-auto-reject'}
										/>
										<Label htmlFor={'bulkUndoAutoReject'} className="text-sm font-[400] font-inter">
											Undo Auto-Reject
										</Label>
									</div>
									<div className="flex items-center gap-2">
										<Radio
											checked={localActionSelect === 'download-resumes'}
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={'downloadResumes'}
											name="bulkAction"
											value={'download-resumes'}
										/>
										<Label htmlFor={'downloadResumes'} className="text-sm font-[400] font-inter">
											Download Resumes
										</Label>
									</div>
								</>
							)}
						</fieldset>
						<Button
							type="submit"
							color="purplePlaced"
							disabled={!localActionSelect || (localActionSelect === 'download-resumes' && bulkActionFn.isLoading)}
							className="bg-[#7777E7] w-full mt-[24px]"
							isProcessing={localActionSelect === 'download-resumes' && bulkActionFn.isLoading}
						>
							{localActionSelect === 'shortlist-profile'
								? 'Shortlist'
								: localActionSelect === 'download-resumes'
									? 'Download'
									: 'Select'}
						</Button>
					</form>
				);
		}
	};

	return (
		<>
			<div className="px-3 py-4">
				<UsersAvatarGroup
					candidates={uniqueCandidates?.map(e => ({
						name: e?.user?.name || '',
						profileImageUrl: e?.user?.profile?.profileImageUrl || e?.user?.profile?.imageUrl || '',
					}))}
				/>

				<div className="h-[1px] bg-[#EBEBF0] my-[12px]"></div>
			</div>
			<div className="px-3 pb-4">{getDrawerContent(selectedBulkAction ?? '')}</div>
		</>
	);
};

export default NewRecruiterCandidatesBulkActionsOnConncetedUsers;
